import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { motion } from "framer-motion";
import { Camera, Upload, ArrowRight, ImageIcon, Loader2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import WebcamCapture from "@/components/visualiser/WebcamCapture";
import { isTouchDevice } from "@/lib/device";
import { useUploadDraft } from "@/contexts/UploadDraftContext";

export default function VisualiserNew() {
  const nav = useNavigate();
  const { draft, hasDraft, clearDraft } = useUploadDraft();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [surface, setSurface] = useState("walls");
  const [projectScope, setProjectScope] = useState("both"); // "interior" | "exterior" | "both"
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [webcamOpen, setWebcamOpen] = useState(false);

  const cameraInputRef = useRef(null);   // mobile / tablet camera input
  const fileInputRef = useRef(null);     // explicit file picker

  const acceptFile = useCallback((f) => {
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setErr("");
  }, []);

  // Stage 2A · Consume a homepage-staged upload if present, so the user never
  // uploads twice. The draft context is authoritative — we only touch it once.
  useEffect(() => {
    if (hasDraft && draft && !file) {
      setFile(draft);
      setPreviewUrl(URL.createObjectURL(draft));
    }
  }, [hasDraft, draft, file]);

  // Drag-and-drop dropzone — DOES NOT auto-open picker on click
  // (we want the explicit Take Photo / Upload buttons to drive that).
  const onDrop = useCallback((accepted) => acceptFile(accepted[0]), [acceptFile]);
  const { getRootProps, getInputProps, isDragActive, open: openDropzone } = useDropzone({
    onDrop, accept: { "image/*": [] }, maxFiles: 1, noClick: true, noKeyboard: true,
  });

  const onTakePhotoClick = async () => {
    setErr("");
    if (isTouchDevice()) {
      // Mobile / tablet → native camera via input capture
      cameraInputRef.current?.click();
      return;
    }
    // Desktop / laptop → feature-detect a real camera before opening the modal.
    // If there's no webcam, fall back silently to the file picker.
    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        fileInputRef.current?.click();
        return;
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasCam = devices.some((d) => d.kind === "videoinput");
      if (hasCam) {
        setWebcamOpen(true);
      } else {
        fileInputRef.current?.click();
      }
    } catch {
      fileInputRef.current?.click();
    }
  };

  const onUploadClick = () => {
    setErr("");
    fileInputRef.current?.click();
  };

  const onWebcamCapture = (capturedFile) => {
    acceptFile(capturedFile);
    setWebcamOpen(false);
  };

  const clearPhoto = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFile(null);
  };

  const submit = async () => {
    if (!file) { setErr("Please take or upload a photo first."); return; }
    setBusy(true);
    setErr("");
    const fd = new FormData();
    fd.append("photo", file);
    fd.append("title", file.name.replace(/\.[^.]+$/, "").slice(0, 60) || "Untitled project");
    fd.append("surface", surface);
    fd.append("project_scope", projectScope);
    try {
      const { data } = await api.post("/visualiser/projects", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Homepage-staged draft (if any) is now safely persisted server-side.
      // Clear it before navigating away.
      if (hasDraft) {
        await clearDraft();
      }
      nav(`/visualiser/p/${data.id}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10 lg:py-16" data-testid="visualiser-new-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-ink mb-2">Start a new project</h1>
        <p className="text-brand-muted mb-2">Take a fresh photo or upload one — whichever you prefer.</p>
        <p className="text-xs text-brand-muted mb-8" data-testid="new-project-hint">
          <span className="font-semibold text-brand-ink">One project = one property.</span> Add a first photo to start — once it&apos;s uploaded you can add up to 8 photos (front, sides, pool area, garage…) all inside this same project. Your free preview unlocks one render anywhere in the project.
        </p>

        {err && (
          <div className="mb-5 bg-destructive/5 border border-destructive/20 text-destructive rounded-2xl px-4 py-3 text-sm" data-testid="new-error">
            {err}
          </div>
        )}

        {/* Hidden inputs — explicit, separate purposes.
            HEIC/HEIF is added explicitly so Apple photos are always
            selectable in the file picker on Chrome / Firefox / macOS. */}
        <input
          ref={cameraInputRef} type="file" accept="image/*,.heic,.heif" capture="environment"
          data-testid="camera-input" className="hidden"
          onChange={(e) => acceptFile(e.target.files?.[0])}
        />
        <input
          ref={fileInputRef} type="file" accept="image/*,.heic,.heif"
          data-testid="file-input" className="hidden"
          onChange={(e) => acceptFile(e.target.files?.[0])}
        />

        {previewUrl ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="relative rounded-[28px] overflow-hidden bg-brand-sand shadow-soft"
            data-testid="new-preview"
          >
            <img src={previewUrl} alt="Your photo" className="w-full max-h-[460px] object-contain" />
            <button onClick={clearPhoto} data-testid="new-clear-photo-btn"
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/95 backdrop-blur shadow-soft flex items-center justify-center text-brand-ink hover:bg-white">
              <X size={16} />
            </button>
          </motion.div>
        ) : (
          <div
            {...getRootProps()}
            className={`relative rounded-[28px] border-2 border-dashed transition-all bg-white px-6 py-12 lg:py-16 text-center ${
              isDragActive ? "border-brand-teal bg-brand-teal/5" : "border-brand-border"
            }`}
            data-testid="new-dropzone"
          >
            <input {...getInputProps()} />
            <div className="w-20 h-20 mx-auto rounded-3xl bg-brand-teal/10 flex items-center justify-center mb-6">
              <ImageIcon size={32} className="text-brand-teal" strokeWidth={1.5} />
            </div>
            <h3 className="text-2xl font-bold text-brand-ink mb-2">Add a photo</h3>
            <p className="text-sm text-brand-muted mb-7 max-w-sm mx-auto">
              JPG, PNG or HEIC · One photo to start · Best results with sharp, well-lit shots
            </p>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:justify-center">
              <button
                type="button"
                onClick={onTakePhotoClick}
                data-testid="take-photo-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand-navy text-white h-12 px-6 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 hover:shadow-navy transition-all tap-spring"
              >
                <Camera size={16} /> Take photo
              </button>
              <button
                type="button"
                onClick={onUploadClick}
                data-testid="upload-file-btn"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white border-2 border-brand-teal h-12 px-6 rounded-2xl text-sm font-semibold text-brand-teal hover:bg-brand-teal/5 transition-all tap-spring"
              >
                <Upload size={16} /> Upload from files
              </button>
            </div>

            <p className="mt-6 text-[11px] text-brand-muted">
              Or drag a file onto this area
            </p>
          </div>
        )}

        {previewUrl && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mt-6 bg-white rounded-3xl p-5 shadow-soft"
          >
            <div className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-3">
              What are you painting?
            </div>
            <div className="grid grid-cols-3 gap-2" data-testid="new-scope-picker">
              {[
                { v: "interior", l: "Interior",    sub: "Walls, ceilings, doors, trims" },
                { v: "exterior", l: "Exterior",    sub: "Walls, fences, garage, fascias" },
                { v: "both",     l: "Both",        sub: "Show all surface options" },
              ].map((s) => (
                <button
                  key={s.v}
                  onClick={() => {
                    setProjectScope(s.v);
                    // sensible surface default per scope
                    setSurface(s.v === "exterior" ? "exterior_walls" : "walls");
                  }}
                  data-testid={`new-scope-${s.v}`}
                  className={`px-3 py-3 rounded-2xl text-left transition-all tap-spring ${
                    projectScope === s.v
                      ? "bg-brand-navy text-white shadow-navy"
                      : "bg-brand-sand text-brand-ink hover:bg-brand-border"
                  }`}
                >
                  <div className="text-sm font-semibold">{s.l}</div>
                  <div className={`text-[10px] mt-0.5 leading-tight ${projectScope === s.v ? "text-white/80" : "text-brand-muted"}`}>{s.sub}</div>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-brand-muted mt-3">You can pick the exact surface (walls, ceiling, doors, etc.) on the next screen.</p>
          </motion.div>
        )}

        {previewUrl && (
          <>
            <button
              onClick={submit} disabled={busy}
              data-testid="new-create-btn"
              className="mt-6 w-full h-14 bg-brand-navy text-white rounded-2xl text-base font-semibold hover:bg-brand-navy/90 hover:shadow-navy disabled:opacity-50 disabled:cursor-not-allowed transition-all tap-spring flex items-center justify-center gap-2"
            >
              {busy ? (<><Loader2 className="animate-spin" size={18} /> Uploading…</>) : (<>Continue <ArrowRight size={18} /></>)}
            </button>
            <p className="mt-4 text-xs text-brand-muted text-center">
              Free preview: 1 colour render included · Unlock for $29.99 AUD to test unlimited colours
            </p>
          </>
        )}
      </motion.div>

      <WebcamCapture
        open={webcamOpen}
        onCapture={onWebcamCapture}
        onClose={() => setWebcamOpen(false)}
        onUploadInstead={() => {
          setWebcamOpen(false);
          // Preserve the user gesture chain — some browsers require the
          // file picker to open in the same task as the click. rAF fires
          // before the next paint but still inside the current gesture.
          requestAnimationFrame(() => fileInputRef.current?.click());
        }}
      />
    </div>
  );
}
